import { type ClassValue, clsx } from "clsx";
import creditCardType, { types as CardTypes } from "credit-card-type";
import currency from "currency.js";
import { format } from "date-fns";
import sodium from "libsodium-wrappers";
import {
  forEach,
  forIn,
  isArray,
  isEmpty,
  isNumber,
  isObject,
  isString,
} from "lodash";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { siteConfig } from "@/config/site";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isLuhn = (input: string) => {
  const digitsOnly = input.replace(/\D/g, "");

  if (digitsOnly === "" || /^0+$/.test(digitsOnly)) {
    return false;
  }

  let sum = 0;
  let shouldDouble = false;

  for (let i = digitsOnly.length - 1; i >= 0; i--) {
    let digit = parseInt(digitsOnly[i]!, 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const formatDateTime = (date: string) => {
  return format(new Date(date), siteConfig.dateTimeFormat);
};

export const formatDate = (date?: string | undefined | null) => {
  if (!date) return null;
  try {
    return format(new Date(date), siteConfig.dateFormat);
  } catch (error) {
    return date;
  }
};

// format number 1,234,567,890.123
export function formatNumber(value: string | number) {
  if (isNaN(Number(value))) {
    return value;
  }
  return new Intl.NumberFormat("en-GB").format(Number(value));
}
export const formatCurrency = (amount: unknown) => {
  let output = Number(amount);
  if (Number.isNaN(Number(output))) return currency(0).format();
  return currency(output).format();
};
export const calculatingPageCount = (
  aggregate: number,
  limit: number | null | undefined
) => {
  return Math.ceil(aggregate / (limit ?? siteConfig.table.limit));
};

/* only use transaction report*/

export function capitalizeFirstLetter(message: string): string {
  if (!message) {
    return message; // Return empty string if input is empty
  }
  return message.charAt(0).toUpperCase() + message.slice(1);
}

export function isNumeric(text: string | number | undefined) {
  if (!text) return false;
  return !isNaN(Number(`${text}`));
}

export function convertToISO8601(date: Date) {
  return date.toISOString().replace(/\.\d{1,3}Z/, "+00:00");
}

const cleanObjHasNoValue = <TObject extends Record<string, any>>(
  obj: TObject,
  options?: {
    isClearStringValue?: boolean;
  }
) => {
  const isClearStringValue = options?.isClearStringValue;
  forIn(obj, (value, key) => {
    const conditionClearValue = isClearStringValue
      ? !isNumber(value) && !isArray(value) && isEmpty(value)
      : !isNumber(value) &&
        !isString(value) &&
        !isArray(value) &&
        isEmpty(value);
    if (conditionClearValue) {
      delete obj[key];
    }
    if (isObject(value)) {
      cleanObjHasNoValue(value, options);
    }
    if (isArray(value)) {
      forEach(value, (childValue, index: number) => {
        if (conditionClearValue) {
          value.splice(index, 1);
        }
        if (isObject(childValue)) {
          cleanObjHasNoValue(childValue, options);
        }
      });
    }
  });
};

export const isMac =
  typeof window !== "undefined"
    ? navigator.platform.toUpperCase().indexOf("MAC") >= 0
    : false;

// Use the library's CardType directly
export type CardType = (typeof CardTypes)[number];

// Display names for card types
const CARD_DISPLAY_NAMES: Record<CardType, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  "american-express": "American Express",
  "diners-club": "Diners Club",
  discover: "Discover",
  jcb: "JCB",
  unionpay: "UnionPay",
  maestro: "Maestro",
  mir: "MIR",
  hiper: "Hiper",
  hipercard: "Hipercard",
  elo: "Elo",
} as const;

export interface CardValidation {
  type: CardType | null;
  displayName: string | null;
  isValid: boolean;
  error?: string;
  gaps?: number[];
  lengths: number[];
  code?: {
    name: string;
    length: number;
  };
}

export function detectCardType(
  cardNumber: string | null | undefined
): CardValidation {
  if (!cardNumber) {
    return {
      type: null,
      displayName: null,
      isValid: false,
      error: "No card number provided",
      lengths: [],
    };
  }

  const cleaned = cardNumber.replace(/\D/g, "");

  if (cleaned.length < 6) {
    return {
      type: null,
      displayName: null,
      isValid: false,
      error: "Card number must be at least 6 digits for identification",
      lengths: [],
    };
  }

  const detected = creditCardType(cleaned);

  const card = detected[0];
  if (!card) {
    return {
      type: null,
      displayName: null,
      isValid: false,
      error: "Unrecognized card type",
      lengths: [],
    };
  }

  const type = card.type as CardType;
  const isValidLength = card.lengths.includes(cleaned.length);
  return {
    type,
    displayName: CARD_DISPLAY_NAMES[type],
    isValid: isValidLength,
    error: isValidLength
      ? undefined
      : `Invalid length for ${CARD_DISPLAY_NAMES[type]}. Expected ${card.lengths.join(" or ")} digits`,
    gaps: card.gaps,
    lengths: card.lengths,
    code: {
      name: card.code.name,
      length: card.code.size,
    },
  };
}
export function countDecimalPlaces(number: number) {
  if (Math.floor(number) === number) {
    return 0; // No decimal places
  }
  const decimalPart = number.toString().split(".")[1];
  return decimalPart ? decimalPart.length : 0;
}

export const formatPhoneWithPrefix = (
  phoneNumber: string | undefined | null
): string | null => {
  if (!phoneNumber) return null;
  return `+${phoneNumber.replace(/\D/g, "")}`;
};

export const percent = (input: string | number) => {
  const inputNumber = Number.isNaN(Number(input)) ? 0 : Number(input);
  return {
    value: inputNumber / 100,
    rawValue: input,
    format: () => `${currency(input).value}%`,
  };
};

export const getIdFromIRI = (iri: string | undefined | null) => {
  if (!iri) return "";
  return iri.split("/").pop() ?? "";
};
export const splitCamelCase = (str: string) =>
  str.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

export function formatToLabelCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase => space
    .replace(/\./g, " - ") // . => ' - '
    .replace(/[_-]+/g, " ") // _ or - => space
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase()) // Capitalize each word
    .replace(/\s+/g, " ")
    .trim();
}
function isBlob(value: unknown) {
  return value instanceof Blob;
}
export function downloadFile(input: unknown, nameFile: string) {
  if (!isBlob(input)) throw new Error("File is not blob");
  const url = URL.createObjectURL(input);
  const a = document.createElement("a");
  a.href = url;
  a.download = nameFile;
  a.click();
  URL.revokeObjectURL(url);
}

export function showErrorMessage(err: unknown, toastId?: string) {
  if (err instanceof Error) {
    return toast.error(err.message || "Something went wrong!", { id: toastId });
  }
  toast.error("Something went wrong!");
}
type TToastMessage = {
  name: string;
  status: "created" | "updated" | "reset" | "deleted";
};
export const toastMessage = {
  success({ name, status }: TToastMessage) {
    return `${name} has been ${status} successfully!`;
  },
  error({ name, status }: TToastMessage) {
    return `${name} could not be ${status}!`;
  },
};

export const makeEncryptedPan = (cardNumber: string) => {
  if (!cardNumber) return "";
  const publicKey = "qN2swkD4nR4rFx3f2tYFPczKUntk1wpx5kACuYLB83I=";
  const keyDecoded = Uint8Array.from(atob(publicKey), (c) => c.charCodeAt(0));
  const encrypted = sodium.crypto_box_seal(cardNumber, keyDecoded);
  return btoa(String.fromCharCode(...encrypted));
};
